/**
 * 059: reduce a phone photo so the platform can accept it.
 *
 * Two failures this fixes, both invisible to the customer and unfixable by them: a 12 MP
 * photo exceeds the platform's 10 MB limit, and an iPhone hands over HEIC, which is not in
 * the accepted set. A camera offers no "smaller photo" setting, so the app does it.
 *
 * The decision layer here is pure and tested. The re-encode itself cannot run in this
 * repository's test environment (jsdom has no `createImageBitmap` and its 2D context is
 * `null`), so it lives behind injected primitives and is confirmed on a real device —
 * see research R1 and quickstart Q17.
 */

export interface Size {
  width: number;
  height: number;
}

/**
 * Whether this file goes through the re-encode at all.
 *
 * Images do — including formats the platform refuses, which is the point: a HEIC becomes a
 * JPEG on the way out. A PDF does not: rendering it into a canvas would turn a document into
 * a picture of its first page. Anything else is left exactly as picked, so the platform's own
 * refusal is what the customer is shown rather than a guess made here.
 */
export function shouldDownscale(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * The size to re-encode at: the long edge capped at `maxEdge`, aspect preserved.
 * An image already within the cap is returned unchanged — never enlarged, which would
 * add bytes and invent detail that was never photographed.
 */
export function targetSize(width: number, height: number, maxEdge: number): Size {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  // Floored at 1: an extreme ratio otherwise rounds the short edge to zero, and a zero-dimension
  // canvas cannot be drawn to. The reduction survives it (encode yields null, the original file is
  // submitted), but a zero-sized encode target should not be reachable in the first place.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** 2000 px on the long edge is ~590 dpi across a Malaysian IC — far more than a reviewer
 *  reading a name and a licence number needs, and roughly 400–700 KB as JPEG. */
const MAX_EDGE = 2000;
const JPEG_QUALITY = 0.85;

/** The browser primitives the re-encode needs, injected so the decision layer stays testable. */
export interface ImagingDeps {
  decode(file: File): Promise<{ width: number; height: number }>;
  encode(source: unknown, size: Size, quality: number): Promise<Blob | null>;
}

export const browserImaging: ImagingDeps = {
  decode: (file) => createImageBitmap(file),
  encode: async (source, size, quality) => {
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(source as CanvasImageSource, 0, 0, size.width, size.height);
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  },
};

function jpegName(name: string): string {
  return `${name.replace(/\.[^.]+$/, '')}.jpg`;
}

/**
 * The file actually submitted: a re-encoded JPEG for an image, the original object for
 * anything else — and the original object again whenever the reduction fails for any
 * reason. Refusing a document here because the browser could not decode it would be the
 * app inventing a rule the platform does not have.
 */
export async function prepareIdentityFile(
  file: File,
  deps: ImagingDeps = browserImaging,
): Promise<File> {
  if (!shouldDownscale(file)) return file;
  try {
    const source = await deps.decode(file);
    const size = targetSize(source.width, source.height, MAX_EDGE);
    const blob = await deps.encode(source, size, JPEG_QUALITY);
    if (!blob) return file;
    return new File([blob], jpegName(file.name), { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
